import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, PawPrint } from "lucide-react";
import { api } from "@/lib/api";
import { ReviewCard } from "@/components/ReviewCard";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    client_name: "",
    pet_name: "",
    rating: 5,
    title: "",
    body: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.title || !form.body) {
      toast.error("Please share your name, a title, and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you for the kind words! 💛");
      setForm({ client_name: "", pet_name: "", rating: 5, title: "", body: "" });
      load();
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : "—";

  return (
    <div data-testid="reviews-page" className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <header className="text-center mb-14">
          <span className="font-script text-2xl text-[#b85b7e]">happy clients</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#3F2A56] mt-2 leading-tight">
            Words from our pet parents.
          </h1>
          <div className="mt-5 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#FFFBF0] border-2 border-[#3F2A56]/15">
            {reviews.length > 0 ? (
              <>
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <span className="font-display text-lg font-semibold text-[#3F2A56]">
                  {avg} / 5 · {reviews.length} review{reviews.length === 1 ? "" : "s"}
                </span>
              </>
            ) : (
              <span className="font-display text-lg font-semibold text-[#3F2A56]">
                Be the first to share your story
              </span>
            )}
          </div>
        </header>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <section data-testid="reviews-list" className="space-y-6">
            {loading ? (
              <div className="parchment-card rounded-3xl p-8 text-center text-[#5C4A73]">
                Fetching the kind words…
              </div>
            ) : reviews.length === 0 ? (
              <div className="parchment-card rounded-3xl p-8 text-center text-[#5C4A73]">
                No reviews yet — be the first!
              </div>
            ) : (
              reviews.map((r, idx) => (
                <ReviewCard key={r.id} review={r} rotation={idx % 2 === 0 ? -0.4 : 0.6} />
              ))
            )}
          </section>

          <aside data-testid="review-form-aside" className="lg:sticky lg:top-28 self-start">
            <form
              onSubmit={onSubmit}
              data-testid="review-form"
              className="parchment-card rounded-3xl p-7 space-y-5"
            >
              <header>
                <span className="font-script text-xl text-[#4A6741]">share your story</span>
                <h2 className="font-display text-2xl font-bold text-[#3F2A56]">
                  Leave a review
                </h2>
                <p className="text-sm text-[#5C4A73] mt-1">
                  Your words help other pet parents feel at ease.
                </p>
              </header>

              <Field label="Your name" required>
                <input
                  data-testid="review-input-name"
                  required
                  value={form.client_name}
                  onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Pet name (optional)">
                <input
                  data-testid="review-input-pet"
                  value={form.pet_name}
                  onChange={(e) => setForm((f) => ({ ...f, pet_name: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Rating">
                <div className="flex items-center gap-1" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-testid={`review-rating-${n}`}
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`w-7 h-7 ${
                          n <= form.rating
                            ? "fill-[#D4AF37] text-[#D4AF37]"
                            : "text-[#3F2A56]/25"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Title" required>
                <input
                  data-testid="review-input-title"
                  required
                  placeholder="A short headline"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Your review" required>
                <textarea
                  data-testid="review-input-body"
                  required
                  rows={4}
                  placeholder="Tell us about your experience…"
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  className={inputCls}
                />
              </Field>

              <button
                type="submit"
                data-testid="submit-review-btn"
                disabled={submitting}
                className="w-full rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-3.5 transition-all hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <PawPrint className="w-4 h-4" />
                {submitting ? "Sharing…" : "Share my review"}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/20 px-4 py-3 text-[#2A1C3A] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-[#4A6741] transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#2A1C3A] mb-1.5">
        {label} {required && <span className="text-[#b85b7e]">*</span>}
      </span>
      {children}
    </label>
  );
}
